/* eslint-disable multiline-ternary */
import {
  Box,
  Button,
  Flex,
  Menu,
  MenuButton,
  MenuItem,
  MenuList,
  Spinner
} from '@chakra-ui/react'
import { useIntl } from 'react-intl'
import {
  ChartBarIcon,
  ChevronDownIcon,
  DocumentTextIcon,
  SparklesIcon,
  WrenchScrewdriverIcon
} from '@heroicons/react/24/outline'
import DisplayModeToggle from './DisplayModeToggle'
import SortOrderToggle from './SortOrderToggle'
import { SHOULD_SHOW_GROWTH_CHART } from '../constants'
import { type DisplayModeType, type SortOrderType } from '../types'
import messages from '../messages'

interface DossierToolbarProps {
  displayMode: DisplayModeType
  onToggleDisplayMode: () => void
  sortOrder: SortOrderType
  onToggleSortOrder: () => void
  hasVisits: boolean
  showCharts: boolean
  onToggleCharts: () => void
  summaryLoading: boolean
  onGenerateSummary: () => void
  onPrescriptionOpen: () => void
}

export default function DossierToolbar({
  displayMode,
  onToggleDisplayMode,
  sortOrder,
  onToggleSortOrder,
  hasVisits,
  showCharts,
  onToggleCharts,
  summaryLoading,
  onGenerateSummary,
  onPrescriptionOpen
}: DossierToolbarProps) {
  const { formatMessage } = useIntl()
  let summaryIcon = <SparklesIcon width="17px" />
  if (summaryLoading) {
    summaryIcon = <Spinner size="xs" />
  }

  return (
    <Flex
      gap={1.5}
      rowGap={1.5}
      wrap="wrap"
      align="center"
      justify={{ base: 'flex-start', '2xl': 'flex-end' }}
      w={{ base: 'full', '2xl': 'auto' }}
      p={{ base: 1, md: 1.5 }}
      bg="white"
      border="1px solid"
      borderColor="gray.200"
      borderRadius="10px"
      boxShadow="sm"
    >
      <DisplayModeToggle
        displayMode={displayMode}
        onToggle={onToggleDisplayMode}
      />
      <SortOrderToggle sortOrder={sortOrder} onToggle={onToggleSortOrder} />
      <Box
        display={{ base: 'none', sm: 'block' }}
        w="1px"
        h="24px"
        bg="gray.200"
        mx={0.5}
      />
      {SHOULD_SHOW_GROWTH_CHART && (
        <Button
          variant="ghost"
          isActive={showCharts}
          _active={{ bg: 'gray.100', color: 'gray.950' }}
          size="sm"
          onClick={onToggleCharts}
          leftIcon={<ChartBarIcon width="17px" />}
        >
          {formatMessage(messages.growthCharts)}
        </Button>
      )}
      {hasVisits && (
        <Menu placement="bottom-end">
          <MenuButton
            as={Button}
            variant="ghost"
            size="sm"
            leftIcon={<WrenchScrewdriverIcon width="17px" />}
            rightIcon={<ChevronDownIcon width="14px" />}
          >
            {formatMessage(messages.clinicalTools)}
          </MenuButton>
          <MenuList minW="210px">
            <MenuItem
              icon={summaryIcon}
              isDisabled={summaryLoading}
              onClick={onGenerateSummary}
            >
              {formatMessage(messages.aiSummary)}
            </MenuItem>
            <MenuItem
              icon={<DocumentTextIcon width="17px" />}
              onClick={onPrescriptionOpen}
            >
              {formatMessage(messages.prescription)}
            </MenuItem>
          </MenuList>
        </Menu>
      )}
    </Flex>
  )
}
